import type { FloorPlanLayout } from './types'

/**
 * Default floor plan layouts for all 3 venues.
 * These are used:
 *   1. As the initial state when the editor opens and no saved layout exists in KV
 *   2. As the fallback when FloorPlanWrapper cannot fetch a custom layout
 *   3. As the "Reset" target in the editor
 *
 * All coordinates are in SVG px units within the given canvasW × canvasH.
 * The layout is the single source of truth for both the editor and the display.
 */

export const DEFAULT_LAYOUTS: Record<string, FloorPlanLayout> = {

  // ────────────────────────────────────────────────────────────────────────────
  // NARRABEEN
  // viewBox: 0 0 860 640
  // ────────────────────────────────────────────────────────────────────────────
  narrabeen: {
    venueId: 'narrabeen',
    canvasW: 860,
    canvasH: 640,

    sections: [
      { kind: 'section', id: 'bar',     name: 'Bar',     colour: 'orange', x: 10,  y: 290, w: 230, h: 250 },
      { kind: 'section', id: 'inside',  name: 'Inside',  colour: 'red',    x: 10,  y: 10,  w: 840, h: 270 },
      { kind: 'section', id: 'outside', name: 'Outside', colour: 'blue',   x: 290, y: 310, w: 560, h: 290 },
    ],

    fixtures: [
      // Kitchen — big black block at bottom-left
      { kind: 'fixture', id: 'kitchen',  fixtureType: 'kitchen',     label: 'KITCHEN', x: 10,  y: 555, w: 265, h: 75, rotation: 0 },
      // Door — small black block top-left
      { kind: 'fixture', id: 'door',     fixtureType: 'door',        label: 'DOOR',    x: 10,  y: 10,  w: 60,  h: 36, rotation: 0 },
      // BAR counter — amber block inside bar section
      { kind: 'fixture', id: 'bar-counter', fixtureType: 'bar_counter', label: 'BAR', x: 135, y: 370, w: 96,  h: 185, rotation: 0 },
      // Window/divider line
      { kind: 'fixture', id: 'window',   fixtureType: 'divider',     label: 'WINDOW',  x: 250, y: 10,  w: 0,   h: 540, rotation: 0 },
      // Tree — circle top-right area
      { kind: 'fixture', id: 'tree',     fixtureType: 'tree',        label: 'TREE',    x: 638, y: 104, w: 96,  h: 96,  rotation: 0 },
      // Cafe 1
      { kind: 'fixture', id: 'cafe-1',   fixtureType: 'generic',     label: 'CAFE 1',  x: 760, y: 45,  w: 90,  h: 50,  colour: 'rgba(58,128,200,0.7)' },
      // Cafe 2
      { kind: 'fixture', id: 'cafe-2',   fixtureType: 'generic',     label: 'CAFE 2',  x: 760, y: 110, w: 90,  h: 50,  colour: 'rgba(58,128,200,0.7)' },
      // Venue title label
      { kind: 'fixture', id: 'title',    fixtureType: 'label',       label: 'NARRABEEN', x: 570, y: 5,  w: 180, h: 30 },
    ],

    tables: [
      // Bar section: 1-4
      { kind: 'table', id: 't1',  label: '1',  capacity: 4, sectionId: 'bar',           shape: 'rect', x: 25,  y: 488, w: 88, h: 52 },
      { kind: 'table', id: 't2',  label: '2',  capacity: 4, sectionId: 'bar',           shape: 'rect', x: 25,  y: 422, w: 88, h: 52 },
      { kind: 'table', id: 't3',  label: '3',  capacity: 4, sectionId: 'bar',           shape: 'rect', x: 25,  y: 356, w: 88, h: 52 },
      { kind: 'table', id: 't4',  label: '4',  capacity: 4, sectionId: 'bar',           shape: 'rect', x: 25,  y: 300, w: 88, h: 46 },
      // Inside: 5-12 (left column) + 20-25 (top right)
      { kind: 'table', id: 't5',  label: '5',  capacity: 4, sectionId: 'inside', shape: 'rect', x: 25,  y: 220, w: 88, h: 58 },
      { kind: 'table', id: 't6',  label: '6',  capacity: 4, sectionId: 'inside', shape: 'rect', x: 25,  y: 156, w: 88, h: 52 },
      { kind: 'table', id: 't7',  label: '7',  capacity: 4, sectionId: 'inside', shape: 'rect', x: 25,  y: 92,  w: 88, h: 52 },
      { kind: 'table', id: 't8',  label: '8',  capacity: 4, sectionId: 'inside', shape: 'rect', x: 130, y: 20,  w: 90, h: 50 },
      { kind: 'table', id: 't9',  label: '9',  capacity: 4, sectionId: 'inside', shape: 'rect', x: 130, y: 84,  w: 90, h: 50 },
      { kind: 'table', id: 't10', label: '10', capacity: 4, sectionId: 'inside', shape: 'rect', x: 130, y: 148, w: 90, h: 50 },
      { kind: 'table', id: 't11', label: '11', capacity: 4, sectionId: 'inside', shape: 'rect', x: 130, y: 212, w: 90, h: 50 },
      { kind: 'table', id: 't12', label: '12', capacity: 4, sectionId: 'inside', shape: 'rect', x: 25,  y: 285, w: 88, h: 52 },
      // Inside continued: 20-25 (top-right area)
      { kind: 'table', id: 't20', label: '20', capacity: 4, sectionId: 'inside', shape: 'rect', x: 300, y: 40,  w: 110, h: 68 },
      { kind: 'table', id: 't21', label: '21', capacity: 4, sectionId: 'inside', shape: 'rect', x: 300, y: 126, w: 110, h: 68 },
      { kind: 'table', id: 't22', label: '22', capacity: 4, sectionId: 'inside', shape: 'rect', x: 300, y: 200, w: 110, h: 68 },
      { kind: 'table', id: 't23', label: '23', capacity: 4, sectionId: 'inside', shape: 'rect', x: 430, y: 40,  w: 110, h: 68 },
      { kind: 'table', id: 't24', label: '24', capacity: 4, sectionId: 'inside', shape: 'rect', x: 430, y: 126, w: 110, h: 68 },
      { kind: 'table', id: 't25', label: '25', capacity: 4, sectionId: 'inside', shape: 'rect', x: 430, y: 200, w: 110, h: 68 },
      // Outside: 26-31 (diagonal cascade)
      { kind: 'table', id: 't26', label: '26', capacity: 4, sectionId: 'outside', shape: 'rect', x: 300, y: 328, w: 90, h: 58 },
      { kind: 'table', id: 't27', label: '27', capacity: 4, sectionId: 'outside', shape: 'rect', x: 402, y: 362, w: 90, h: 58 },
      { kind: 'table', id: 't28', label: '28', capacity: 4, sectionId: 'outside', shape: 'rect', x: 504, y: 396, w: 90, h: 58 },
      { kind: 'table', id: 't29', label: '29', capacity: 4, sectionId: 'outside', shape: 'rect', x: 606, y: 430, w: 90, h: 58 },
      { kind: 'table', id: 't30', label: '30', capacity: 4, sectionId: 'outside', shape: 'rect', x: 708, y: 464, w: 90, h: 58 },
      { kind: 'table', id: 't31', label: '31', capacity: 4, sectionId: 'outside', shape: 'rect', x: 710, y: 536, w: 90, h: 52 },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────────
  // DEE WHY
  // viewBox: 0 0 820 600
  // ────────────────────────────────────────────────────────────────────────────
  'dee-why': {
    venueId: 'dee-why',
    canvasW: 820,
    canvasH: 610,

    sections: [
      { kind: 'section', id: 'bar',       name: 'Bar',       colour: 'orange', x: 8,   y: 8,   w: 220, h: 490 },
      { kind: 'section', id: 'pool-area', name: 'Pool Area', colour: 'green',  x: 238, y: 8,   w: 260, h: 490 },
      { kind: 'section', id: 'inside',    name: 'Inside',    colour: 'red',    x: 508, y: 8,   w: 300, h: 490 },
      { kind: 'section', id: 'outside',   name: 'Outside',   colour: 'blue',   x: 8,   y: 508, w: 804, h: 84  },
    ],

    fixtures: [
      // BAR counter — amber block inside bar section, lower area
      { kind: 'fixture', id: 'bar-counter', fixtureType: 'bar_counter', label: 'BAR',  x: 16,  y: 410, w: 68,  h: 80,  rotation: 0 },
      // Door — angled, below bar counter
      { kind: 'fixture', id: 'door',        fixtureType: 'door',        label: 'DOOR', x: 16,  y: 490, w: 68,  h: 34,  rotation: -8 },
      // Pool table — bookable
      {
        kind: 'fixture', id: 'pool-table', fixtureType: 'pool_table', label: 'POOL TABLE',
        x: 262, y: 80, w: 208, h: 310, rotation: 0,
        poolTableId: 'deewhy-pool-1',
      },
      // Venue title
      { kind: 'fixture', id: 'title', fixtureType: 'label', label: 'DEE WHY', x: 560, y: 5, w: 160, h: 30 },
    ],

    tables: [
      // Bar: 1-7
      { kind: 'table', id: 't1',  label: '1',  capacity: 4, sectionId: 'bar',       shape: 'rect', x: 24,  y: 40,  w: 88, h: 52 },
      { kind: 'table', id: 't2',  label: '2',  capacity: 4, sectionId: 'bar',       shape: 'rect', x: 24,  y: 104, w: 88, h: 52 },
      { kind: 'table', id: 't3',  label: '3',  capacity: 4, sectionId: 'bar',       shape: 'rect', x: 24,  y: 168, w: 88, h: 52 },
      { kind: 'table', id: 't4',  label: '4',  capacity: 4, sectionId: 'bar',       shape: 'rect', x: 24,  y: 232, w: 88, h: 52 },
      { kind: 'table', id: 't5',  label: '5',  capacity: 4, sectionId: 'bar',       shape: 'rect', x: 122, y: 40,  w: 88, h: 52 },
      { kind: 'table', id: 't6',  label: '6',  capacity: 4, sectionId: 'bar',       shape: 'rect', x: 122, y: 104, w: 88, h: 52 },
      { kind: 'table', id: 't7',  label: '7',  capacity: 4, sectionId: 'bar',       shape: 'rect', x: 122, y: 168, w: 88, h: 52 },
      // Pool area: 8-10 (flanking the pool table)
      { kind: 'table', id: 't8',  label: '8',  capacity: 4, sectionId: 'pool-area', shape: 'rect', x: 248, y: 30,  w: 86, h: 42 },
      { kind: 'table', id: 't9',  label: '9',  capacity: 4, sectionId: 'pool-area', shape: 'rect', x: 248, y: 418, w: 86, h: 52 },
      { kind: 'table', id: 't10', label: '10', capacity: 4, sectionId: 'pool-area', shape: 'rect', x: 385, y: 418, w: 86, h: 52 },
      // Inside: 11-21
      { kind: 'table', id: 't11', label: '11', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 518, y: 40,  w: 96, h: 52 },
      { kind: 'table', id: 't12', label: '12', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 518, y: 104, w: 96, h: 52 },
      { kind: 'table', id: 't13', label: '13', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 518, y: 168, w: 96, h: 52 },
      { kind: 'table', id: 't14', label: '14', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 518, y: 232, w: 96, h: 52 },
      { kind: 'table', id: 't15', label: '15', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 518, y: 296, w: 96, h: 52 },
      { kind: 'table', id: 't16', label: '16', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 518, y: 360, w: 96, h: 52 },
      { kind: 'table', id: 't17', label: '17', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 518, y: 424, w: 96, h: 52 },
      { kind: 'table', id: 't18', label: '18', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 622, y: 40,  w: 96, h: 52 },
      { kind: 'table', id: 't19', label: '19', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 622, y: 104, w: 96, h: 52 },
      { kind: 'table', id: 't20', label: '20', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 622, y: 168, w: 96, h: 52 },
      { kind: 'table', id: 't21', label: '21', capacity: 4, sectionId: 'inside',    shape: 'rect', x: 622, y: 232, w: 96, h: 52 },
      // Outside: 22-30 (bottom row)
      { kind: 'table', id: 't22', label: '22', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 16,  y: 524, w: 88, h: 52 },
      { kind: 'table', id: 't23', label: '23', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 112, y: 524, w: 88, h: 52 },
      { kind: 'table', id: 't24', label: '24', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 208, y: 524, w: 88, h: 52 },
      { kind: 'table', id: 't25', label: '25', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 304, y: 524, w: 88, h: 52 },
      { kind: 'table', id: 't26', label: '26', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 400, y: 524, w: 88, h: 52 },
      { kind: 'table', id: 't27', label: '27', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 496, y: 524, w: 88, h: 52 },
      { kind: 'table', id: 't28', label: '28', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 592, y: 524, w: 88, h: 52 },
      { kind: 'table', id: 't29', label: '29', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 688, y: 524, w: 60, h: 52 },
      { kind: 'table', id: 't30', label: '30', capacity: 4, sectionId: 'outside',   shape: 'rect', x: 752, y: 524, w: 60, h: 52 },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────────
  // MANLY
  // viewBox: 0 0 560 580
  // ────────────────────────────────────────────────────────────────────────────
  manly: {
    venueId: 'manly',
    canvasW: 560,
    canvasH: 580,

    sections: [
      { kind: 'section', id: 'bar',    name: 'Bar',    colour: 'orange', x: 8, y: 8,   w: 220, h: 280 },
      { kind: 'section', id: 'inside', name: 'Inside', colour: 'blue',   x: 8, y: 298, w: 544, h: 274 },
    ],

    fixtures: [
      // BAR counter — amber block in the bar section
      { kind: 'fixture', id: 'bar-counter', fixtureType: 'bar_counter', label: 'BAR',        x: 16,  y: 210, w: 56,  h: 68,  rotation: 0 },
      // Pool table — bookable
      {
        kind: 'fixture', id: 'pool-table', fixtureType: 'pool_table', label: 'POOL TABLE',
        x: 160, y: 390, w: 200, h: 140, rotation: 0,
        poolTableId: 'manly-pool-1',
      },
      // Venue title
      { kind: 'fixture', id: 'title', fixtureType: 'label', label: 'MANLY', x: 200, y: 5, w: 100, h: 30 },
    ],

    tables: [
      // Bar: 1-2
      { kind: 'table', id: 't1', label: '1', capacity: 4, sectionId: 'bar',     shape: 'rect', x: 80,  y: 40,  w: 110, h: 68 },
      { kind: 'table', id: 't2', label: '2', capacity: 4, sectionId: 'bar',     shape: 'rect', x: 80,  y: 122, w: 110, h: 68 },
      // Inside: 3-8
      { kind: 'table', id: 't3', label: '3', capacity: 4, sectionId: 'inside', shape: 'rect', x: 20,  y: 318, w: 100, h: 60 },
      { kind: 'table', id: 't4', label: '4', capacity: 4, sectionId: 'inside', shape: 'rect', x: 128, y: 318, w: 100, h: 60 },
      { kind: 'table', id: 't5', label: '5', capacity: 4, sectionId: 'inside', shape: 'rect', x: 420, y: 318, w: 100, h: 60 },
      { kind: 'table', id: 't6', label: '6', capacity: 4, sectionId: 'inside', shape: 'rect', x: 420, y: 392, w: 100, h: 60 },
      { kind: 'table', id: 't7', label: '7', capacity: 4, sectionId: 'inside', shape: 'rect', x: 20,  y: 478, w: 120, h: 56 },
      { kind: 'table', id: 't8', label: '8', capacity: 4, sectionId: 'inside', shape: 'rect', x: 150, y: 478, w: 120, h: 56 },
    ],
  },
}
